import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { api, backendOrigin } from "@/api/client";
import { DateField, MenuField } from "@/components/Fields";

const money = (value) => {
  if (value == null) return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  const fraction = Number.isInteger(amount) ? 0 : 2;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: fraction, maximumFractionDigits: 2 }).format(amount);
};
const stages = ["Enquiry", "Design", "3D Visualisation", "Costing & Order", "Workshop", "Quality Check", "Dispatch", "Installation"];
const fileTypes = new Set(["pdf", "png", "jpg", "jpeg", "webp", "gif", "heic", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv", "txt", "dwg", "dxf", "skp", "3dm", "rvt", "ifc", "zip"]);
const fileLimit = 20 * 1024 * 1024;
const statusOptions = [
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On hold" },
  { value: "lost", label: "Lost" },
];
const statusLabel = (status) => statusOptions.find((option) => option.value === status)?.label || (status === "confirmed" ? "Confirmed" : "Active");
const modes = { bank_transfer: "Bank transfer", upi: "UPI", cheque: "Cheque", cash: "Cash", card: "Card", other: "Other" };

const when = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const text = String(value);
  const hasTime = text.includes("T") || text.includes(":");
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(hasTime ? { hour: "2-digit", minute: "2-digit", hour12: true } : {}),
  }).format(date);
};

const sizeLabel = (bytes) => {
  const amount = Number(bytes || 0);
  if (amount < 1024) return `${amount} B`;
  if (amount < 1024 * 1024) return `${Math.max(1, Math.round(amount / 1024))} KB`;
  return `${(amount / (1024 * 1024)).toFixed(1)} MB`;
};

const attached = (message) => {
  const list = Array.isArray(message.attachments) ? message.attachments : [];
  return list.map((item) => (typeof item === "string" ? { id: item, filename: "File" } : item)).filter((item) => item && item.id);
};

export default function Project({ user }) {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [versions, setVersions] = useState([]);
  const [activity, setActivity] = useState([]);
  const [people, setPeople] = useState([]);
  const [pick, setPick] = useState("");
  const [tasks, setTasks] = useState([]);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskAssignee, setTaskAssignee] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [reason, setReason] = useState("");
  const [live, setLive] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ description: "", qty: "1", rate: "", discount: "0", gst: "18" });
  const [pay, setPay] = useState({ amount: "", received_on: "", mode: "bank_transfer", reference: "" });
  const [rework, setRework] = useState("");
  const drawing = useRef(null);
  const drawingKind = useRef("design");
  const commercial = ["admin", "management", "sales"].includes(user.role);

  const load = async () => {
    const response = await api.get(`/projects/${orderId}`);
    setData(response.data);
    return response.data;
  };

  useEffect(() => {
    let ignore = false;
    api.get(`/projects/${orderId}`).then((response) => {
      if (!ignore) setData(response.data);
    }).catch((err) => {
      if (!ignore) setError(err.response?.data?.detail || "This project is not on your desk.");
    });
    return () => { ignore = true; };
  }, [orderId]);

  useEffect(() => { setError(""); }, [tab]);

  useEffect(() => {
    if (tab === "chat") {
      let closed = false;
      let socket;
      let retry;
      const pull = () => api.get(`/projects/${orderId}/messages`).then((response) => {
        setMessages(response.data);
        const last = response.data[response.data.length - 1];
        if (last) api.post(`/projects/${orderId}/messages/read`, { last_read_message_id: last.id }).catch(() => {});
      }).catch(() => {});
      const connect = () => {
        const socketUrl = `${backendOrigin || window.location.origin}`.replace(/^http/, "ws") + `/api/ws/projects/${orderId}`;
        socket = new WebSocket(socketUrl);
        socket.onopen = () => { if (!closed) setLive(true); };
        socket.onmessage = () => pull();
        socket.onclose = () => {
          setLive(false);
          if (!closed) retry = setTimeout(connect, 2000);
        };
      };
      pull();
      const timer = setInterval(pull, 5000);
      connect();
      return () => { closed = true; clearInterval(timer); clearTimeout(retry); socket?.close(); setLive(false); };
    }
    if (tab === "files") api.get(`/projects/${orderId}/files`).then((response) => setFiles(response.data)).catch(() => setFiles([]));
    if (tab === "quotes") api.get(`/projects/${orderId}/quotations`).then((response) => setQuotes(response.data));
    if (tab === "payments") api.get(`/projects/${orderId}/advances`).then((response) => setAdvances(response.data));
    if (tab === "design") api.get(`/projects/${orderId}/versions`).then((response) => setVersions(response.data));
    if (tab === "activity") api.get(`/projects/${orderId}/activity`).then((response) => setActivity(response.data));
    if (tab === "tasks") api.get(`/projects/${orderId}/tasks`).then((response) => setTasks(response.data));
    return undefined;
  }, [tab, orderId]);

  useEffect(() => {
    if (!data?.can_admin) return undefined;
    api.get(`/projects/${orderId}/assignable-users`).then((response) => setPeople(response.data)).catch(() => {});
    return undefined;
  }, [data?.can_admin, orderId]);

  if (!data) return <div className="loading">{error || "Opening the project…"}</div>;
  const project = data.project;
  const admin = data.can_admin;
  const canShift = admin || data.can_advance;

  const changeStatus = async (status) => {
    if (!status || status === (project.status || "active")) return;
    setError("");
    try {
      await api.post(`/projects/${orderId}/status`, { status, note });
      await load();
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not change the status.");
    }
  };

  const move = async (toStage) => {
    if (toStage === project.stage) return;
    if (project.status === "confirmed" && toStage < 4) { setError("Unconfirm the order before moving it back."); return; }
    if (Math.abs(toStage - project.stage) > 1) { setError("Move one stage at a time."); return; }
    if (!note.trim()) { setError("Add a note for this stage change."); return; }
    setError("");
    try {
      await api.post(`/projects/${orderId}/stage`, { to_stage: toStage, from_stage: project.stage, note });
      setNote("");
      try {
        await load();
      } catch (err) {
        if (err.response?.status !== 404) throw err;
        setData((current) => current && ({
          ...current,
          can_advance: false,
          project: { ...current.project, stage: toStage, stage_name: stages[toStage - 1] || current.project.stage_name },
        }));
        setError("This job has moved on and is no longer on your desk.");
      }
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not move this project.");
    }
  };

  const confirm = async () => {
    setError("");
    try {
      await api.post(`/projects/${orderId}/confirm`, { note });
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail?.missing ? `Still needed: ${detail.missing.join(", ")}` : detail || "The order is not ready to confirm.");
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!draft.trim()) return;
    await api.post(`/projects/${orderId}/messages`, { body: draft.trim() });
    setDraft("");
    const response = await api.get(`/projects/${orderId}/messages`);
    setMessages(response.data);
  };

  const addMember = async () => {
    if (!pick) return;
    setError("");
    try {
      await api.post(`/projects/${orderId}/members`, { user_ids: [pick] });
      setPick("");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not add this person.");
    }
  };

  const saveQuote = async (event) => {
    event.preventDefault();
    const qty = Number(quoteForm.qty);
    const gst = Number(quoteForm.gst);
    const rate = Number(quoteForm.rate);
    if (!quoteForm.description.trim() || !rate) { setError("Add a description and a rate."); return; }
    if (!(qty > 0)) { setError("Quantity must be greater than zero."); return; }
    if (Math.abs(rate * 100 - Math.round(rate * 100)) > 0.001) { setError("Enter the rate in rupees and paise."); return; }
    if (rate > 10000000) { setError("Rate cannot be more than ₹1,00,00,000."); return; }
    if (Number.isNaN(gst) || gst < 0) { setError("GST cannot be negative."); return; }
    setError("");
    try {
      await api.post(`/projects/${orderId}/quotations`, {
        items: [{ description: quoteForm.description.trim(), qty, rate, unit: "nos" }],
        discount_type: "percent",
        discount_value: Number(quoteForm.discount || 0),
        gst_rate: gst,
      });
      setQuoteForm({ description: "", qty: "1", rate: "", discount: "0", gst: "18" });
      setQuotes((await api.get(`/projects/${orderId}/quotations`)).data);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save the quotation.");
    }
  };

  const approveQuote = async (quote) => {
    const replacing = quotes.some((item) => item.id !== quote.id && item.status === "approved");
    if (replacing && !window.confirm(`Another quotation is already approved. Approving v${quote.version_no} replaces it. Continue?`)) return;
    setError("");
    try {
      if (quote.status === "draft") await api.post(`/projects/${orderId}/quotations/${quote.id}/send`);
      await api.post(`/projects/${orderId}/quotations/${quote.id}/feedback`, {
        outcome: "approved", channel: "call", client_person: project.client, feedback_date: new Date().toISOString().slice(0, 10), note: "Client approved",
      });
      setQuotes((await api.get(`/projects/${orderId}/quotations`)).data);
      await load();
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not approve this quotation.");
    }
  };

  const chooseDrawing = (kind) => {
    drawingKind.current = kind;
    drawing.current?.click();
  };

  const uploadDrawing = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const extension = file.name.split(".").pop().toLowerCase();
    if (!fileTypes.has(extension)) { setError("This file type is not allowed. Use a drawing, photo, PDF, or office document."); return; }
    if (file.size > fileLimit) { setError("Files must be 20 MB or smaller."); return; }
    const body = new FormData();
    body.append("file", file);
    body.append("category", "drawing");
    setError("");
    try {
      const uploaded = await api.post(`/projects/${orderId}/files/uploads/direct`, body);
      await api.post(`/projects/${orderId}/versions`, { kind: drawingKind.current, title: file.name, notes: note, file_ids: [uploaded.data.id] });
      setVersions((await api.get(`/projects/${orderId}/versions`)).data);
      setFiles((await api.get(`/projects/${orderId}/files`)).data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(err.response?.status === 413 ? "Files must be 20 MB or smaller." : (typeof detail === "string" ? detail : "Could not upload this drawing."));
    }
  };

  const approveVersion = async (version) => {
    await api.post(`/projects/${orderId}/versions/${version.id}/feedback`, {
      outcome: "approved", channel: "call", client_person: project.client, feedback_date: new Date().toISOString().slice(0, 10), note: note || "Approved",
    });
    setVersions((await api.get(`/projects/${orderId}/versions`)).data);
    await load();
  };

  const downloadFile = async (file) => {
    const meta = await api.get(`/projects/${orderId}/files/${file.id}/download`);
    if (meta.data.mode === "url" && meta.data.url) {
      window.open(meta.data.url, "_blank", "noopener");
      return;
    }
    const response = await api.get(`/projects/${orderId}/files/${file.id}/content`, { responseType: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(response.data);
    link.download = meta.data.filename || file.filename || "file";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const uploadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const extension = file.name.split(".").pop().toLowerCase();
    if (!fileTypes.has(extension)) { setError("This file type is not allowed. Use a drawing, photo, PDF, or office document."); return; }
    if (file.size > fileLimit) { setError("Files must be 20 MB or smaller."); return; }
    const body = new FormData();
    body.append("file", file);
    body.append("category", "general");
    setUploading(true);
    setError("");
    try {
      await api.post(`/projects/${orderId}/files/uploads/direct`, body);
      setFiles((await api.get(`/projects/${orderId}/files`)).data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(err.response?.status === 413 ? "Files must be 20 MB or smaller." : (typeof detail === "string" ? detail : "Could not upload this file."));
    } finally {
      setUploading(false);
    }
  };

  const addAdvance = async (event) => {
    event.preventDefault();
    if (!Number(pay.amount) || !pay.received_on) { setError("Add the amount and the date it was received."); return; }
    setError("");
    try {
      await api.post(`/projects/${orderId}/advances`, {
        amount: Number(pay.amount),
        received_on: pay.received_on,
        mode: pay.mode,
        reference: pay.reference,
      });
      setPay({ amount: "", received_on: "", mode: "bank_transfer", reference: "" });
      setAdvances((await api.get(`/projects/${orderId}/advances`)).data);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not record the advance.");
    }
  };

  return (
    <div className="project-page" data-testid="project-page">
      <p className="eyebrow"><Link to="/pipeline">Projects</Link> · {project.enquiry_reference || project.reference}</p>
      <div className="section-head">
        <div>
          <h1 data-testid="page-title">{project.title}</h1>
          <p className="subtle">{project.client} · {project.location || project.city}</p>
        </div>
        <div className="project-actions">
          <div className="status-change">
            <span>Current status</span>
            {admin && project.status !== "confirmed" ? (
              <MenuField label="" value={project.status || "active"} onChange={changeStatus} testId="project-status" options={statusOptions} />
            ) : (
              <span className="status-current" data-testid="project-status">{statusLabel(project.status)}</span>
            )}
          </div>
          {admin && project.stage === 4 && project.status !== "confirmed" && (
            <button className="brass-button" disabled={!data.gate.ready} onClick={confirm} title={data.gate.ready ? "Confirm this order" : data.gate.items.filter((item) => !item.ok).map((item) => item.label).join(", ")} data-testid="confirm-order-button">Confirm order <ChevronRight size={16} /></button>
          )}
        </div>
      </div>
      <div className="stage-track">
        <div className="stage-track-head">
          <span>Current stage</span>
          {project.status === "confirmed" && <span className="stage-pill" data-testid="confirmed-badge">Confirmed · {project.reference}</span>}
        </div>
        <div className="stage-stepper" aria-label="Current stage">
          {stages.map((label, index) => {
            const position = index + 1;
            const state = project.stage > position ? "done" : project.stage === position ? "now" : "ahead";
            return (
              <div key={label} className={state} data-testid={`stage-step-${position}`}>
                <i>{position}</i>
                <span>{label}</span>
                {state === "now" && <small>Now</small>}
              </div>
            );
          })}
        </div>
        <p className="stage-now">This job is in {stages[project.stage - 1] || project.stage_name}.</p>
        {canShift && (
          <div className="stage-change">
            <label>Note for a stage change<input value={note} onChange={(event) => setNote(event.target.value)} data-testid="stage-note-input" /></label>
            <MenuField label="" buttonLabel="Change current stage" value={String(project.stage)} onChange={(value) => move(Number(value))} testId="change-stage" options={stages.map((label, index) => ({ value: String(index + 1), label })).filter((option) => admin ? Math.abs(Number(option.value) - project.stage) <= 1 : Number(option.value) === project.stage + 1)} />
          </div>
        )}
        {["admin", "management", "qc"].includes(user.role) && project.stage === 6 && (
          <form className="stage-change" onSubmit={async (event) => {
            event.preventDefault();
            if (!rework.trim()) { setError("Add the reason for sending this back to the workshop."); return; }
            setError("");
            try {
              await api.post(`/orders/${orderId}/qc-rework`, { reason: rework.trim() });
              setRework("");
              await load();
            } catch (err) {
              setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not send this job back.");
            }
          }}>
            <label>Reason for rework<input value={rework} onChange={(event) => setRework(event.target.value)} data-testid="project-rework-reason" /></label>
            <button className="outline-button" data-testid="project-rework">Send back to workshop</button>
          </form>
        )}
      </div>
      {error && <div className="form-error" data-testid="project-error">{error}</div>}
      <div className="project-layout">
        <section>
          <div className="tabs project-tabs">
            {[["overview", "Overview"], ["chat", "Chat"], ["files", "Files"], ["design", "Design"], ["quotes", "Quotes"], ["payments", "Payments"], ["tasks", "Tasks"], ["activity", "Activity"]].filter(([item]) => commercial || (item !== "quotes" && item !== "payments")).map(([item, label]) => (
              <button key={item} className={tab === item ? "active" : ""} onClick={() => setTab(item)} data-testid={`project-tab-${item}`}>{label}</button>
            ))}
          </div>
          {tab === "overview" && (
            <div className="panel">
              <div className="project-facts">
                {[
                  ["Project Name (Reference, if any)", project.title],
                  ["Location", project.location || project.city],
                  ["Architect", project.architect],
                  ["Timeline", project.timeline],
                  ["Client Name", project.client],
                  ["Project Management Company Name (Optional)", project.pmc_name],
                  ["Person Assigned from Client Side", project.client_contact_name],
                  ["Person Assigned from Architect's Office", project.architect_contact],
                  ["Area of Project", project.project_area],
                  ["Quantity of Furniture", project.furniture_qty],
                  ["Material Preference", project.material_preference],
                  ["Collection Selected (if any)", project.collection],
                  ["Any Bespoke Requirements", project.bespoke_requirements, true],
                  ["3D Options to Be Given", project.three_d_options, true],
                  ["Salesperson Involved", project.salesperson_name],
                  ["Point of Contact from Client Side", project.client_point_of_contact],
                  ["Value", commercial ? money(project.value) : "—"],
                  ["Opened", when(project.created_at)],
                ].map(([label, value, wide]) => (
                  <div key={label} className={wide ? "span-2" : ""}>
                    <span>{label}</span>
                    <strong>{value || "—"}</strong>
                  </div>
                ))}
              </div>
              {(data.stock || []).length > 0 && (
                <div data-testid="project-stock">
                  <h2>Stock sent to this job</h2>
                  {data.stock.map((row) => (
                    <div className="leave-row" key={row.id}>
                      <div><strong>{row.item_name}</strong><span>{row.quantity}{row.unit ? ` ${row.unit}` : ""}{row.note ? ` · ${row.note}` : ""} · {when(row.created_at)}</span></div>
                    </div>
                  ))}
                </div>
              )}
              <h2>Confirm order</h2>
              {data.gate.items.map((item) => (
                <div className={`gate-row ${item.ok ? "gate-done" : "gate-open"}`} key={item.code} data-testid={`gate-${item.code}`}>
                  <div><strong>{item.label}</strong><span>{item.detail}</span></div>
                  <i className="gate-tick">{item.ok ? "Ready" : "Needed"}</i>
                </div>
              ))}
              {admin && project.status === "confirmed" && project.stage === 4 && (
                <form onSubmit={async (event) => {
                  event.preventDefault();
                  if (!reason.trim()) { setError("Add the reason for unconfirming."); return; }
                  try {
                    await api.post(`/projects/${orderId}/unconfirm`, { note: reason.trim() });
                    setReason("");
                    await load();
                  } catch (err) {
                    setError(err.response?.data?.detail || "Could not unconfirm this order.");
                  }
                }}>
                  <label>Reason to unconfirm<input value={reason} onChange={(event) => setReason(event.target.value)} data-testid="unconfirm-reason" /></label>
                  <button className="outline-button" data-testid="unconfirm-button">Unconfirm</button>
                </form>
              )}
            </div>
          )}
          {tab === "chat" && (
            <div className="panel">
              <p className="eyebrow" data-testid="chat-live">{live ? "Live" : "Reconnecting…"}</p>
              <div className="chat-log" data-testid="chat-log">
                {messages.map((message) => (
                  <div className={`chat-line${message.deleted ? " deleted" : ""}`} key={message.id}>
                    <div className="chat-meta">
                      <strong>{message.author_name}</strong>
                      <time className="stamp" dateTime={message.created_at}>{when(message.created_at)}{message.edited_at ? " · edited" : ""}</time>
                    </div>
                    <p>{message.deleted ? "Message deleted" : message.body}</p>
                    {!message.deleted && attached(message).map((file) => (
                      <button className="chat-file" type="button" key={file.id} onClick={() => downloadFile(file)}>{file.filename}</button>
                    ))}
                  </div>
                ))}
                {messages.length === 0 && <p className="subtle">No messages yet.</p>}
              </div>
              <form onSubmit={sendMessage}>
                <label>Message<textarea value={draft} onChange={(event) => setDraft(event.target.value)} data-testid="chat-input" /></label>
                <button className="brass-button" data-testid="chat-send">Send</button>
              </form>
            </div>
          )}
          {tab === "files" && (
            <div className="panel" data-testid="project-files">
              <label className="file-upload">
                <input type="file" onChange={uploadFile} disabled={uploading} data-testid="project-file-input" />
                <span className="brass-button">{uploading ? "Uploading…" : "Upload a file"}</span>
              </label>
              {files.map((file) => (
                <div className="leave-row" key={file.id} data-testid={`project-file-${file.id}`}>
                  <div>
                    <strong>{file.original_filename}</strong>
                    <span>{file.uploaded_by_name || "Workshop"} · {sizeLabel(file.size_bytes)} · {when(file.completed_at || file.created_at)}</span>
                  </div>
                  <button className="text-button" type="button" onClick={() => downloadFile(file)}>Download</button>
                </div>
              ))}
              {files.length === 0 && <p className="subtle">No files on this project yet. An upload is posted in the project chat as well.</p>}
            </div>
          )}
          {tab === "design" && (
            <div className="panel">
              <div className="drawer-actions"><input ref={drawing} type="file" hidden data-testid="design-file-input" onChange={uploadDrawing} /><button className="outline-button" type="button" onClick={() => chooseDrawing("design")} data-testid="add-design-version">Upload design version</button><button className="outline-button" type="button" onClick={() => chooseDrawing("3d")} data-testid="add-3d-version">Upload 3D version</button></div>
              {versions.map((version) => (
                <div className="leave-row" key={version.id}>
                  <div><strong>{version.kind} v{version.version_no}</strong><span>{version.client_status}{version.is_final ? " · final" : ""} · {when(version.created_at)}</span></div>
                  {(admin || user.role === "sales") && version.client_status !== "approved" && <button className="outline-button" type="button" onClick={() => approveVersion(version)} data-testid={`approve-version-${version.version_no}`}>Log approval</button>}
                  {admin && version.kind === "design" && version.client_status === "approved" && !version.is_final && <button className="outline-button" type="button" onClick={() => api.post(`/projects/${orderId}/versions/${version.id}/mark-final`).then(() => load())} data-testid="mark-final-design">Mark final</button>}
                </div>
              ))}
              {versions.length === 0 && <p className="subtle">No versions yet. Drawings will attach here once file storage is connected.</p>}
            </div>
          )}
          {tab === "quotes" && (
            <div className="panel">
              {(admin || user.role === "sales") && (
                <form className="project-form quote-form" onSubmit={saveQuote} data-testid="quote-form">
                  <label>Description<input value={quoteForm.description} onChange={(event) => setQuoteForm({ ...quoteForm, description: event.target.value })} data-testid="quote-description" /></label>
                  <label>Qty<input value={quoteForm.qty} onChange={(event) => setQuoteForm({ ...quoteForm, qty: event.target.value })} inputMode="decimal" data-testid="quote-qty" /></label>
                  <label>Rate<input value={quoteForm.rate} onChange={(event) => setQuoteForm({ ...quoteForm, rate: event.target.value })} inputMode="decimal" data-testid="quote-rate" /></label>
                  <label>Discount %<input value={quoteForm.discount} onChange={(event) => setQuoteForm({ ...quoteForm, discount: event.target.value })} inputMode="decimal" data-testid="quote-discount" /></label>
                  <label>GST %<input value={quoteForm.gst} onChange={(event) => setQuoteForm({ ...quoteForm, gst: event.target.value })} inputMode="decimal" data-testid="quote-gst" /></label>
                  <button className="brass-button" data-testid="quote-save">Save draft</button>
                </form>
              )}
              {quotes.map((quote) => (
                <div className="leave-row" key={quote.id} data-testid={`quote-${quote.version_no}`}>
                  <div><strong>{quote.quote_number || `v${quote.version_no}`} · {quote.status}</strong><span>{money(quote.total)} · GST {money(quote.gst_amount)} · {when(quote.created_at)}</span></div>
                  {(admin || user.role === "sales") && project.status !== "confirmed" && project.stage <= 4 && (quote.status === "draft" || quote.status === "sent") && <button className="outline-button" type="button" onClick={() => approveQuote(quote)} data-testid={`approve-quote-${quote.version_no}`}>Send and approve</button>}
                  {(admin || user.role === "sales") && quote.status === "sent" && <button className="outline-button" type="button" onClick={() => api.post(`/projects/${orderId}/quotations/${quote.id}/revise`).then(() => api.get(`/projects/${orderId}/quotations`)).then((response) => setQuotes(response.data))} data-testid={`revise-quote-${quote.version_no}`}>Revise</button>}
                </div>
              ))}
            </div>
          )}
          {tab === "payments" && (
            <div className="panel">
              {(admin || user.role === "sales") && (
                <form className="project-form pay-form" onSubmit={addAdvance} data-testid="advance-form">
                  <label>Amount<input value={pay.amount} onChange={(event) => setPay({ ...pay, amount: event.target.value })} inputMode="decimal" data-testid="project-advance-amount" /></label>
                  <DateField label="Received on" value={pay.received_on} onChange={(received_on) => setPay({ ...pay, received_on })} testId="project-advance-date" />
                  <MenuField label="Mode" value={pay.mode} onChange={(mode) => setPay({ ...pay, mode })} testId="project-advance-mode" options={[{ value: "bank_transfer", label: "Bank transfer" }, { value: "upi", label: "UPI" }, { value: "cheque", label: "Cheque" }, { value: "cash", label: "Cash" }, { value: "card", label: "Card" }]} />
                  <label>Reference<input value={pay.reference} onChange={(event) => setPay({ ...pay, reference: event.target.value })} data-testid="project-advance-reference" /></label>
                  <button className="brass-button" type="submit" data-testid="record-advance">Record advance</button>
                </form>
              )}
              {advances.map((row) => (
                <div className="leave-row" key={row.id}>
                  <div>
                    <strong>{money(row.amount)} · {modes[row.mode] || row.mode}</strong>
                    <span>{row.reference || "No reference"} · received {when(row.received_on)} · recorded {when(row.recorded_at)}{row.recorded_by_name ? ` · ${row.recorded_by_name}` : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {tab === "tasks" && (
            <div className="panel" data-testid="project-tasks">
              <p className="subtle">Assigned here, the task stays on this project. A general task, with no project, is assigned from the Tasks page.</p>
              {admin && (
                <form onSubmit={async (event) => {
                  event.preventDefault();
                  if (!taskTitle.trim() || !taskAssignee || !taskDue) { setError("Add a title, a member, and a due date."); return; }
                  try {
                    await api.post(`/projects/${orderId}/tasks`, { title: taskTitle.trim(), assignee_id: taskAssignee, due_date: taskDue, priority: "Normal" });
                    setTaskTitle("");
                    setTasks((await api.get(`/projects/${orderId}/tasks`)).data);
                    setError("");
                  } catch (err) {
                    setError(err.response?.data?.detail || "Could not assign this task.");
                  }
                }} className="project-form task-form" data-testid="project-task-form">
                  <label>Task<input value={taskTitle} onChange={(event) => setTaskTitle(event.target.value)} data-testid="project-task-title" placeholder="What needs doing" /></label>
                  <MenuField label="Assign to" value={taskAssignee} onChange={setTaskAssignee} testId="project-task-assignee" options={data.members.map((member) => ({ value: member.user_id, label: member.name }))} />
                  <DateField label="Due" value={taskDue} onChange={setTaskDue} testId="project-task-due" />
                  <button className="brass-button" data-testid="project-task-save">Assign</button>
                </form>
              )}
              {admin && data.members.length === 0 && <p className="subtle">Add someone on the right before a task can be assigned.</p>}
              {tasks.map((task) => {
                const done = task.status === "Approved";
                const canFinish = user.role === "admin" || user.role === "management" || task.assignee_id === user.id || task.created_by === user.id;
                return (
                  <div className="leave-row" key={task.id} data-testid={`project-task-${task.id}`}>
                    <div><strong>{task.title}</strong><span>{task.assignee_name} · due {when(task.due_date)} · assigned {when(task.created_at)}{done ? " · Done" : ""}{task.assignee_flag ? ` · ${task.assignee_flag}` : ""}</span></div>
                    {canFinish && <button className="outline-button" type="button" onClick={async () => {
                      try {
                        await api.patch(`/tasks/${task.id}`, { status: done ? "Open" : "Approved" });
                        setTasks((await api.get(`/projects/${orderId}/tasks`)).data);
                        setError("");
                      } catch (err) {
                        setError(err.response?.data?.detail || "Could not update this task.");
                      }
                    }} data-testid={`project-task-done-${task.id}`}>{done ? "Reopen" : "Mark done"}</button>}
                  </div>
                );
              })}
              {tasks.length === 0 && <p className="subtle">No tasks on this project yet.</p>}
            </div>
          )}
          {tab === "activity" && (
            <div className="audit-list">{activity.map((row) => <div className="audit-row" key={row.id}><time className="stamp audit-time">{when(row.created_at)}</time><div><strong>{row.actor_name}</strong><span>{String(row.action || "").replaceAll("_", " ")}</span></div></div>)}</div>
          )}
        </section>
        <aside className="panel">
          <p className="eyebrow">Members</p>
          {data.members.map((member) => (
            <div className="leave-row" key={member.user_id}>
              <div><strong>{member.name}</strong><span>{member.designation || member.role}{member.added_at ? ` · added ${when(member.added_at)}` : ""}</span></div>
              {admin && <button className="text-button" type="button" onClick={() => api.delete(`/projects/${orderId}/members/${member.user_id}`).then(load)} data-testid={`remove-member-${member.user_id}`}>Remove</button>}
            </div>
          ))}
          {admin && (
            <div className="member-add">
              <MenuField label="Add someone" value={pick} onChange={setPick} testId="add-member-select" options={people.filter((person) => !data.members.some((member) => member.user_id === person.id)).map((person) => ({ value: person.id, label: person.name }))} />
              <button className="outline-button" type="button" onClick={addMember} data-testid="add-member-button">Add member</button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
